import { createContext, useContext, useState, useMemo, useCallback, useEffect, type ReactNode } from "react";
import type {
  RecoverUserNameResponse,
  FactorItems,
} from "@fmc-digital/auth-api-generated";
import { setAnalyticsEmailId } from "@/utils/analyticsUserContext";

interface RecoverUsernameContextType {
  response: RecoverUserNameResponse | null;
  guid: string | null;
  factorsList: FactorItems[];
  email: string | null;
  lastName: string | null;
  setResponse: (response: RecoverUserNameResponse | null) => void;
  setEmail: (email: string | null) => void;
  setLastName: (lastName: string | null) => void;
  clearResponse: () => void;
}

const RecoverUsernameContext = createContext<
  RecoverUsernameContextType | undefined
>(undefined);

export function RecoverUsernameProvider({ children }: { readonly children: ReactNode }) {
  const [response, setResponse] = useState<RecoverUserNameResponse | null>(
    null,
  );
  const [email, setEmail] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);

  useEffect(() => {
    setAnalyticsEmailId(email);
  }, [email]);

  const clearResponse = useCallback(() => {
    setResponse(null);
    setEmail(null);
    setLastName(null);
  }, []);

  // Normalize factorsList to always be an array
  // The API might return a single object or an array
  const factorsList: FactorItems[] = useMemo(() => {
    if (!response?.factorsList) {
      return [];
    }
    if (Array.isArray(response.factorsList)) {
      return response.factorsList;
    }
    return [response.factorsList];
  }, [response?.factorsList]);

  const value: RecoverUsernameContextType = useMemo(
    () => ({
      response,
      guid: response?.guid ?? null,
      factorsList,
      email,
      lastName,
      setResponse,
      setEmail,
      setLastName,
      clearResponse,
    }),
    [response, factorsList, email, lastName, setEmail, clearResponse],
  );

  return (
    <RecoverUsernameContext.Provider value={value}>
      {children}
    </RecoverUsernameContext.Provider>
  );
}

export function useRecoverUsername() {
  const context = useContext(RecoverUsernameContext);
  if (context === undefined) {
    throw new Error(
      "useRecoverUsername must be used within a RecoverUsernameProvider",
    );
  }
  return context;
}
